// DatePicker (DESIGN §3.6.7): Feld wie Input mit CalendarBlank, Format „25 Oct 2026“, freie Eingabe
// wird geparst (ungültig → Meldung), Popover mit Monatskalender und optionaler Schnellwahl.
// DateRangePicker wählt einen Bereich (Fläche `accent-soft` zwischen den Enden).
import { useId, useState, type Ref } from 'react';
import { CalendarBlankIcon, CaretDownIcon } from '@phosphor-icons/react';
import { Popover as PopoverPrimitive } from 'radix-ui';
import { formatDisplay, fromIso, parseDateInput, quickPicks, toIso } from './_lib/calendar';
import {
  fieldFrame,
  fieldInput,
  fieldTrigger,
  fieldIcon,
  type FieldSize,
} from './_lib/fieldStyles';
import { useFieldProps } from './_lib/fieldContext';
import { floatingSurface } from './_lib/menuStyles';
import { Button } from './Button';
import { Calendar } from './Calendar';
import { IconButton } from './IconButton';
import './_lib/motion.css';
import { useLayerStyle } from './_lib/layer';
import { cn, today as appToday } from '../basis/kern';

interface CommonProps {
  /** „Heute“ als ISO-Datum; in der App `APP_TODAY` (AUFTRAG §3). Standard: Systemdatum. */
  today?: string;
  min?: string;
  max?: string;
  /** Schnellwahl rechts (Today, +1 week, End of quarter). */
  quickPicks?: boolean;
  size?: FieldSize;
  disabled?: boolean;
  id?: string;
  'aria-label'?: string;
  className?: string;
}

export interface DatePickerProps extends CommonProps {
  ref?: Ref<HTMLInputElement>;
  /** ISO-Datum „yyyy-MM-dd“ oder null. */
  value: string | null;
  onValueChange: (value: string | null) => void;
  onBlur?: () => void;
  placeholder?: string;
  name?: string;
}

const panelClass = cn(floatingSurface, 'rounded-xl p-3');

/** „Heute“ nach APP_TODAY (configureFormat beim Plattform-Start), nicht nach der Browser-Uhr. */
const todayIso = () => appToday().toISOString().slice(0, 10);

function QuickPicks({ today, onPick }: { today: string; onPick: (iso: string) => void }) {
  const base = fromIso(today) ?? new Date();
  return (
    <div className="flex flex-col gap-1 border-l border-line-subtle pl-3 max-sm:flex-row max-sm:border-l-0 max-sm:pl-0">
      {quickPicks(base).map((pick) => (
        <Button
          key={pick.label}
          variant="ghost"
          surface="card"
          size="sm"
          onClick={() => onPick(toIso(pick.date))}
          className="justify-start"
        >
          {pick.label}
        </Button>
      ))}
    </div>
  );
}

export function DatePicker({
  ref,
  value,
  onValueChange,
  onBlur,
  today = todayIso(),
  min,
  max,
  quickPicks: withQuickPicks = false,
  size = 'md',
  disabled,
  placeholder = 'e.g. 25 Oct 2026',
  name,
  className,
  ...rest
}: DatePickerProps) {
  const errorId = useId();
  const field = useFieldProps({ id: rest.id });
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<string | null>(null);
  const [invalid, setInvalid] = useState(false);
  const layerStyle = useLayerStyle();
  const selected = fromIso(value);
  const display = selected ? formatDisplay(selected) : '';

  const pick = (iso: string) => {
    onValueChange(iso);
    setDraft(null);
    setInvalid(false);
    setOpen(false);
  };

  const commitDraft = () => {
    if (draft === null) return;
    if (draft.trim() === '') {
      onValueChange(null);
      setInvalid(false);
    } else {
      const parsed = parseDateInput(draft);
      const iso = parsed ? toIso(parsed) : null;
      const bounded = iso && (min === undefined || iso >= min) && (max === undefined || iso <= max);
      if (iso && bounded) onValueChange(iso);
      setInvalid(!bounded);
      if (!bounded) return;
    }
    setDraft(null);
  };

  const describedBy = cn(field['aria-describedby'], invalid && errorId) || undefined;
  return (
    <div className={cn('flex w-full min-w-0 flex-col gap-1', className)}>
      <PopoverPrimitive.Root open={open} onOpenChange={setOpen}>
        <PopoverPrimitive.Anchor asChild>
          <div className={fieldFrame(size, 'pr-1.5')}>
            <span aria-hidden="true" className={fieldIcon}>
              <CalendarBlankIcon />
            </span>
            <input
              ref={ref}
              id={field.id}
              name={name}
              disabled={disabled}
              value={draft ?? display}
              placeholder={placeholder}
              aria-label={rest['aria-label']}
              aria-describedby={describedBy}
              aria-invalid={invalid || field['aria-invalid'] || undefined}
              autoComplete="off"
              onChange={(event) => setDraft(event.target.value)}
              onBlur={() => {
                commitDraft();
                onBlur?.();
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter') commitDraft();
                if (event.key === 'ArrowDown' && event.altKey) setOpen(true);
              }}
              className={cn(fieldInput, 'tabular-nums')}
            />
            <PopoverPrimitive.Trigger asChild>
              <IconButton
                label="Choose date"
                icon={<CaretDownIcon />}
                variant="ghost"
                surface="tile"
                size="sm"
                tooltip={false}
                disabled={disabled}
              />
            </PopoverPrimitive.Trigger>
          </div>
        </PopoverPrimitive.Anchor>
        <PopoverPrimitive.Portal>
          <PopoverPrimitive.Content
            align="start"
            sideOffset={8}
            collisionPadding={16}
            style={layerStyle}
            className={panelClass}
          >
            <div className="flex gap-3 max-sm:flex-col">
              <Calendar
                selected={value}
                today={today}
                min={min}
                max={max}
                onSelect={pick}
                focusOnMount
              />
              {withQuickPicks ? <QuickPicks today={today} onPick={pick} /> : null}
            </div>
          </PopoverPrimitive.Content>
        </PopoverPrimitive.Portal>
      </PopoverPrimitive.Root>
      {invalid ? (
        <span id={errorId} className="text-label text-danger-fg">
          Enter a date like 25 Oct 2026{min || max ? ' within the allowed range' : ''}.
        </span>
      ) : null}
    </div>
  );
}

export interface DateRange {
  from: string | null;
  to: string | null;
}

export interface DateRangePickerProps extends CommonProps {
  value: DateRange;
  onValueChange: (value: DateRange) => void;
  placeholder?: string;
}

/** Bereichswahl: erster Klick setzt den Anfang, zweiter das Ende (vertauscht, falls davor). */
export function DateRangePicker({
  value,
  onValueChange,
  today = todayIso(),
  min,
  max,
  size = 'md',
  disabled,
  placeholder = 'Select dates',
  className,
  ...rest
}: DateRangePickerProps) {
  const field = useFieldProps({ id: rest.id });
  const [open, setOpen] = useState(false);
  const layerStyle = useLayerStyle();
  const from = fromIso(value.from);
  const to = fromIso(value.to);
  const label = from ? `${formatDisplay(from)} – ${to ? formatDisplay(to) : '…'}` : null;

  const select = (iso: string) => {
    if (!value.from || value.to) return onValueChange({ from: iso, to: null });
    const [a, b] = iso < value.from ? [iso, value.from] : [value.from, iso];
    onValueChange({ from: a, to: b });
    setOpen(false);
  };

  return (
    <PopoverPrimitive.Root open={open} onOpenChange={setOpen}>
      <PopoverPrimitive.Trigger
        id={field.id}
        disabled={disabled}
        aria-label={rest['aria-label']}
        aria-describedby={field['aria-describedby']}
        className={cn(fieldTrigger(size), className)}
      >
        <span aria-hidden="true" className={fieldIcon}>
          <CalendarBlankIcon />
        </span>
        <span
          className={cn('min-w-0 flex-1 truncate tabular-nums', !label && 'text-fg-placeholder')}
        >
          {label ?? placeholder}
        </span>
      </PopoverPrimitive.Trigger>
      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content
          align="start"
          sideOffset={8}
          collisionPadding={16}
          style={layerStyle}
          className={panelClass}
        >
          <Calendar
            selected={value.from}
            rangeEnd={value.to}
            today={today}
            min={min}
            max={max}
            onSelect={select}
            focusOnMount
          />
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  );
}
